import { Model } from "@/types/a2i-media.types";

const PROVIDER_SELECTION_ORDER: Record<string, number> = {
  gemini: 0,
  openai: 1,
  byteplus: 2,
  replicate: 3,
};

const MODEL_SELECTION_ORDER: Record<string, number> = {
  // Gemini image models: full current models first, lower-cost Lite last.
  "gemini-3.1-flash-image": 0,
  "gemini-3.1-flash-image-preview": 0,
  "gemini-3-pro-image": 1,
  "gemini-3-pro-image-preview": 1,
  "gemini-2.5-flash-image": 2,
  "gemini-2.5-flash-image-preview": 2,
  "gemini-3.1-flash-lite-image": 3,
  "imagen-4.0-ultra-generate-001": 4,
  "imagen-4.0-generate-001": 5,
  "imagen-4.0-fast-generate-001": 6,

  // OpenAI image models.
  "gpt-image-2": 0,
  "gpt-image-1": 1,

  // Seedream image models.
  "seedream-5-0-260128": 0,
  "seedream-4-5-251128": 1,
  "seedream-4-0-250828": 2,
  "seedream-3-0-t2i-250415": 3,
  "seededit-3-0-i2i-250628": 4,

  // FLUX image models and KittyKat FLUX fine-tunes.
  "black-forest-labs/flux-1.1-pro-ultra": 0,
  "black-forest-labs/flux-1.1-pro": 1,
  "black-forest-labs/flux-dev": 2,
  "black-forest-labs/flux-1.1-pro-ultra-finetuned": 3,

  // Google video models.
  "veo-3.1-generate-001": 0,
  "veo-3.1-generate-preview": 0,
  "veo-3.1-fast-generate-001": 1,
  "veo-3.1-fast-generate-preview": 1,
  "veo-3.0-generate-001": 2,
  "veo-2.0-generate-001": 3,

  // Seedance video models.
  "bytedance/seedance-2.0": 0,
  "seedance-1-5-pro-251215": 1,
  "seedance-1-0-pro-250528": 2,
  "seedance-1-0-pro-fast-251015": 3,
  "seedance-1-0-lite-i2v-250428": 4,

  // Kling video models.
  "kwaivgi/kling-v3-video": 0,
  "kwaivgi/kling-v2.5-turbo-pro": 1,
  "kwaivgi/kling-v1.5-pro": 2,
  "kwaivgi/kling-v1.5-standard": 3,
};

/**
 * Sort models for display without changing catalogue availability, stable IDs,
 * defaults, pricing, or provider request data.
 */
export function sortModelsForSelection(models: Model[]): Model[] {
  return models
    .map((model, index) => ({ model, index }))
    .sort((left, right) => {
      const leftProvider = left.model.icon_provider ?? left.model.provider;
      const rightProvider = right.model.icon_provider ?? right.model.provider;
      const providerDifference =
        (PROVIDER_SELECTION_ORDER[leftProvider] ?? Number.MAX_SAFE_INTEGER) -
        (PROVIDER_SELECTION_ORDER[rightProvider] ?? Number.MAX_SAFE_INTEGER);
      if (providerDifference !== 0) return providerDifference;

      const modelDifference =
        (MODEL_SELECTION_ORDER[left.model.model] ?? Number.MAX_SAFE_INTEGER) -
        (MODEL_SELECTION_ORDER[right.model.model] ?? Number.MAX_SAFE_INTEGER);
      if (modelDifference !== 0) return modelDifference;

      return left.index - right.index;
    })
    .map(({ model }) => model);
}

export function modelMatchesProviderId(model: Model, modelId?: string): boolean {
  if (!modelId) return false;
  return (
    model.model === modelId || model.legacy_model_ids?.includes(modelId) === true
  );
}

export function findModelByProviderId(
  models: Model[],
  modelId?: string,
  type?: Model["type"],
): Model | undefined {
  return models.find(
    (model) =>
      (!type || model.type === type) && modelMatchesProviderId(model, modelId),
  );
}

export function findModelByCatalogueId(
  models: Model[],
  catalogueId?: string | null,
  type?: Model["type"],
): Model | undefined {
  if (!catalogueId) return undefined;
  return models.find(
    (model) =>
      (!type || model.type === type) &&
      (model.id === catalogueId ||
        model.legacy_catalogue_ids?.includes(catalogueId) === true),
  );
}
